import type { SupabaseClient } from "@supabase/supabase-js";

/** Identidade da conta Instagram conectada ao workspace (para ocultar comentários próprios na UI). */
export interface ConnectedInstagramIdentity {
  igUserId: string | null;
  usernameNorm: string | null;
}

export function normalizeIgUsername(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== "string") return null;
  const s = raw.trim().toLowerCase().replace(/^@/, "");
  return s.length > 0 ? s : null;
}

/** true se o comentário foi feito pela conta conectada (deve ser ocultado na inbox de comentários). */
export function isCommentFromConnectedAccount(
  row: { from_user_id: string; from_username: string | null },
  identity: ConnectedInstagramIdentity | null,
): boolean {
  if (!identity) return false;
  const hasId = identity.igUserId != null && identity.igUserId !== "";
  const hasUser = identity.usernameNorm != null && identity.usernameNorm !== "";
  if (!hasId && !hasUser) return false;
  if (hasId && row.from_user_id === identity.igUserId) return true;
  const fromU = normalizeIgUsername(row.from_username);
  if (hasUser && fromU != null && fromU === identity.usernameNorm) return true;
  return false;
}

export async function fetchConnectedInstagramIdentity(
  client: SupabaseClient,
  workspaceId: string,
): Promise<ConnectedInstagramIdentity | null> {
  const { data, error } = await client
    .from("integrations")
    .select("instagram_account_id, config")
    .eq("workspace_id", workspaceId)
    .eq("provider", "instagram")
    .eq("status", "active")
    .maybeSingle();

  if (error || !data) return null;

  const cfg = data.config as { username?: string } | null | undefined;
  return {
    igUserId: data.instagram_account_id ?? null,
    usernameNorm: normalizeIgUsername(cfg?.username),
  };
}
